import cx from "classnames";
import isNull from "lodash/isNull";
import {
  IonAlert,
  IonContent,
  IonLabel,
  IonModal,
  IonPage,
  IonProgressBar,
  useIonRouter,
} from "@ionic/react";
import {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import s from "./SuccessRetryPaymentListener.module.scss";
import brandLogo from "../../../../../../Common/assets/logo/wl-woodmark-logo-rev.svg";
import confettiAnimation from "../../../../../assets/animations/confetti.json";
import useSuccessRetryPaymentListenerProvider from "./provider";
import useSuccessRetryPaymentListenerStore from "./store";
// import { DASHBOARD } from "../../../../../constants/routes";

import { useWLSubscriptionResponseDialog } from "../../../../../components/WLSubscriptionResponseDialog/store";
import { useFormsStore } from "../../../../../../Common/store/common";
import { useEffectOnlyOnce } from "../../../../../../Common/utils/common";
import useWLPromoStore from "../../../../../components/WLPromoField/store";
import { signOut } from "../../../../../services/user";
import { WELCOME_BACK } from "../../../../../constants/routes";
import { usePurchaseV2Store } from "../../store";
import { SUBSCRIPTION_PRODUCT_NAME } from "../../RetryPaymentProvider/RetryPaymentProviderWStripeForm/store";
import WLLottie from "../../../../../../AppUser/components/WLLottie/WLLottie";
import {
  getSubscriptionHeroIcon,
  SubscriptionType,
} from "../../../../../../Common/constants/subscriptionConst";

const SuccessRetryPaymentListener = () => {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const productNameRef = useRef<string | null>(null);

  useSuccessRetryPaymentListenerProvider();

  const {
    successfulPurchase,
    inProcessRetryPayment,
    showPaymentErrorAlert,
    setInProcessRetryPayment,
    setShowPaymentErrorAlert,
    reset,
  } = useSuccessRetryPaymentListenerStore();
  const { selectedProduct, createAndRetryPaymentInvoice } =
    usePurchaseV2Store();

  const { setFormLoading } = useFormsStore();

  const name = (productNameRef.current ||
    selectedProduct?.name ||
    "Starter") as SubscriptionType;
  const cardIcon = getSubscriptionHeroIcon(name);

  const [fakeProgress, setFakeProgress] = useState(0);
  const [fakeProgressTimer, setFakeProgressTimer] = useState(
    null as null | NodeJS.Timeout
  );
  const fakeProgressTimerRef = useRef(fakeProgressTimer);
  fakeProgressTimerRef.current = fakeProgressTimer;

  const [fakeInitProgressTimer, setFakeInitProgressTimer] = useState(
    null as null | NodeJS.Timeout
  );
  const fakeInitProgressTimerRef = useRef(fakeInitProgressTimer);
  fakeInitProgressTimerRef.current = fakeInitProgressTimer;

  const [showSuccess, setShowSuccess] = useState(false);

  const router = useIonRouter();

  const dismiss = () => {
    modalRef.current?.dismiss();
    reset();
    setFakeProgress(0);
    setFakeProgressTimer(null);
    setFakeInitProgressTimer(null);
    clearInterval(fakeInitProgressTimerRef.current || undefined);
    clearInterval(fakeProgressTimerRef.current || undefined);
    setFormLoading(false);

    if (!showPaymentErrorAlert) {
      // router.push(DASHBOARD, "back", "pop");
      // router.goBack();
    }
  };

  const startFakeProgress = useCallback(() => {
    if (!isNull(fakeProgressTimerRef.current)) {
      clearInterval(fakeProgressTimerRef.current);
    }
    const interval = setInterval(() => {
      setFakeProgress((prevProgress) => {
        return (prevProgress += 1);
      });
    }, 30);
    setFakeProgressTimer(interval);
  }, []);

  useEffect(() => {
    if (
      fakeProgress >= 56 &&
      !successfulPurchase &&
      !!fakeInitProgressTimerRef.current
    ) {
      clearInterval(fakeInitProgressTimerRef.current);
    } else if (fakeProgress >= 100 && !isNull(fakeProgressTimerRef.current)) {
      clearInterval(fakeProgressTimerRef.current);
      setShowSuccess(true);

      if (!!fakeInitProgressTimerRef.current) {
        clearInterval(fakeInitProgressTimerRef.current);
      }
    }
  }, [fakeProgress]);

  const startFakeInitPRogress = useCallback(() => {
    console.log("will start fake inint progress");
    if (!isNull(fakeInitProgressTimerRef.current)) {
      clearInterval(fakeInitProgressTimerRef.current);
    }
    const interval = setInterval(() => {
      console.log("interval runnin on fake init");
      setFakeProgress((prevProgress) => {
        return (prevProgress += 1);
      });
    }, 30);
    setFakeInitProgressTimer(interval);
  }, []);

  useEffect(() => {
    if (successfulPurchase && isNull(fakeProgressTimerRef.current)) {
      startFakeProgress();
      setFormLoading(false);
      setShowPaymentErrorAlert(false);
    } else if (
      !!inProcessRetryPayment &&
      isNull(fakeInitProgressTimerRef.current)
    ) {
      startFakeInitPRogress();
    }
  }, [successfulPurchase, inProcessRetryPayment]);

  useEffectOnlyOnce(() => {
    productNameRef.current = new URLSearchParams(window.location.search).get(
      SUBSCRIPTION_PRODUCT_NAME
    );
  });

  const handleSignOutRedirect = async () => {
    await signOut();
    dismiss();
    router.push(WELCOME_BACK, "forward", "pop");
  };

  return (
    <IonModal
      className={s.modal}
      onDidDismiss={dismiss}
      ref={modalRef}
      isOpen={!!inProcessRetryPayment}
    >
      <IonContent className={s.content} scrollY={false} fullscreen>
        <div className={s.body}>
          <div className={s.detailsContainer}>
            <div className={s.labelsContainer}>
              <IonLabel
                className={cx(s.subscriptionLbl, "ion-text-uppercase", {
                  [s.longName]: !!name && name.length > 4,
                })}
              >
                {name || ""}
              </IonLabel>
              <IonLabel className={s.memberLbl}>MEMBER</IonLabel>
            </div>

            {!!showSuccess && (
              <Suspense>
                <WLLottie
                  className={s.confettiAnimation}
                  animationData={confettiAnimation}
                  justOnce={true}
                  onComplete={() => {
                    const { showDialog: showSubscriptionResponseDialog } =
                      useWLSubscriptionResponseDialog.getState();

                    showSubscriptionResponseDialog({
                      title: "SUCCESS",
                      description:
                        "Done and dusted! Your membership is active again.",
                      icon: name,
                      onConfirm: async () => {
                        const resetPromo =
                          useWLPromoStore.getState().resetPromoField;
                        if (resetPromo !== undefined) {
                          resetPromo();
                        }
                        await handleSignOutRedirect();
                      },
                      onConfirmText: "Let's Go !",
                    });
                  }}
                />
              </Suspense>
            )}
            <div className={s.progressContainer}>
              <img src={cardIcon} className={s.subscriptionIcon} alt="" loading="lazy" decoding="async" />
              <IonProgressBar
                className={s.progressBar}
                type={"determinate"}
                value={fakeProgress / 100}
              />
              <IonLabel
                className={cx(
                  s.progressText,
                  "wl-h7 golden ion-text-uppercase ion-text-center"
                )}
              >
                {`Hold tight, you're about to go ${name}!`}
              </IonLabel>
            </div>
          </div>
          <img className={s.wlLogo} src={brandLogo} alt="" loading="eager" decoding="async" />
        </div>

        <IonAlert
          isOpen={showPaymentErrorAlert && fakeProgress >= 56}
          header="Payment Failed"
          message={"Please try again or change your payment method"}
          backdropDismiss={false}
          buttons={[
            {
              text: "Cancel",
              role: "destructive",
              handler: () => {
                setInProcessRetryPayment(false);
              },
            },
            {
              text: "Try Again",
              role: "confirm",
              handler: () => {
                if (!!fakeInitProgressTimerRef.current) {
                  clearInterval(fakeInitProgressTimerRef.current);
                }
                if (!!fakeProgressTimerRef.current) {
                  clearInterval(fakeProgressTimerRef.current);
                }
                setFakeProgressTimer(null);
                setFakeInitProgressTimer(null);
                setFakeProgress(0);
                startFakeInitPRogress();
                setShowPaymentErrorAlert(false);
                createAndRetryPaymentInvoice();
              },
            },
          ]}
        />
      </IonContent>
    </IonModal>
  );
};

export default memo(SuccessRetryPaymentListener);
